import React, { useRef, useCallback } from "react";
import { useCanvasLoop } from "../shared/useCanvasLoop";
import { mulberry32 } from "@/lib/simulation/rng";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
interface StarParticle {
  x: number;
  y: number;
  z: number;
  size: number;
  color: string;
}
export interface UniverseHeroProps {
  className?: string;
}
export default function UniverseHero({
  className = ""
}: UniverseHeroProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prefersReduced = useReducedMotion();
  const particlesRef = useRef<{
    particles: StarParticle[];
    initialized: boolean;
  }>({
    particles: [],
    initialized: false
  });
  const initParticles = useCallback((w: number) => {
    const rng = mulberry32(98231);
    const count = w < 768 ? 220 : 450;
    const particles: StarParticle[] = [];

    // Spiral galaxy disc + outer halo
    for (let i = 0; i < count; i++) {
      // Arms parameter
      const armIndex = i % 2;
      const armOffset = armIndex * Math.PI;
      const dist = Math.pow(rng(), 1.7) * 260 + 10;
      const angle = dist * 0.025 + armOffset + (rng() - 0.5) * 0.7;

      // Disc thickness
      const discY = (rng() - 0.5) * (40 - dist * 0.08);
      const x = Math.cos(angle) * dist;
      const z = Math.sin(angle) * dist;
      const y = discY;

      // Color distribution: cyan core, blue arms, violet fringes, silver dust
      const cPick = rng();
      const color = cPick < 0.35 ? "#00e5ff" // cyan
      : cPick < 0.65 ? "#4d7cff" // blue
      : cPick < 0.85 ? "#a78bfa" // violet
      : "#e6ebf5"; // silver

      particles.push({
        x,
        y,
        z,
        size: 0.7 + rng() * 1.5,
        color
      });
    }
    particlesRef.current = {
      particles,
      initialized: true
    };
  }, []);
  const draw = useCallback((ctx: CanvasRenderingContext2D, w: number, h: number, timeMs: number) => {
    if (!particlesRef.current.initialized) {
      initParticles(w);
    }
    ctx.clearRect(0, 0, w, h);
    const cx = w / 2;
    const cy = h / 2;

    // Slow 3D rotation angle
    const rotationSpeed = prefersReduced ? 0 : timeMs * 0.00015;
    const tiltX = 0.55; // inclined view angle (pitch)
    const cosTilt = Math.cos(tiltX);
    const sinTilt = Math.sin(tiltX);
    const cosRot = Math.cos(rotationSpeed);
    const sinRot = Math.sin(rotationSpeed);

    // Faint coordinate grid plane under the universe
    ctx.save();
    const gridRadius = Math.min(w * 0.42, 280);
    const rings = 4;
    for (let r = 1; r <= rings; r++) {
      const rad = gridRadius * r / rings;
      ctx.beginPath();
      // Elliptical projected ring
      ctx.ellipse(cx, cy, rad, rad * sinTilt, 0, 0, Math.PI * 2);
      ctx.strokeStyle = r === rings ? "rgba(0, 229, 255, 0.22)" : "rgba(77, 124, 255, 0.12)";
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Radial grid rays
    const rays = 8;
    for (let i = 0; i < rays; i++) {
      const angle = i * Math.PI * 2 / rays + (prefersReduced ? 0 : rotationSpeed * 0.3);
      const rx = Math.cos(angle) * gridRadius;
      const rz = Math.sin(angle) * gridRadius;
      const projX = cx + rx;
      const projY = cy + rz * sinTilt;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(projX, projY);
      ctx.strokeStyle = "rgba(0, 229, 255, 0.09)";
      ctx.stroke();
    }
    ctx.restore();

    // Core glow
    const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 70);
    coreGrad.addColorStop(0, "rgba(0, 229, 255, 0.35)");
    coreGrad.addColorStop(0.4, "rgba(77, 124, 255, 0.18)");
    coreGrad.addColorStop(1, "rgba(5, 6, 15, 0)");
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, 70, 0, Math.PI * 2);
    ctx.fill();

    // Draw projected particles
    const {
      particles
    } = particlesRef.current;
    const fov = 420;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];

      // 1. Rotate around Y axis
      const rotX = p.x * cosRot - p.z * sinRot;
      const rotZ = p.x * sinRot + p.z * cosRot;

      // 2. Tilt around X axis (pitch)
      const tiltedY = p.y * cosTilt - rotZ * sinTilt;
      const tiltedZ = p.y * sinTilt + rotZ * cosTilt;

      // 3. Perspective projection
      const cameraZ = tiltedZ + 480;
      if (cameraZ > 10) {
        const scale = fov / cameraZ;
        const projX = cx + rotX * scale;
        const projY = cy + tiltedY * scale;
        if (projX >= 0 && projX <= w && projY >= 0 && projY <= h) {
          const depthAlpha = Math.max(0.15, Math.min(1, 1 - tiltedZ / 500));
          ctx.fillStyle = p.color;
          ctx.globalAlpha = depthAlpha;
          ctx.beginPath();
          ctx.arc(projX, projY, Math.max(0.8, p.size * scale * 0.9), 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1.0;
  }, [initParticles, prefersReduced]);
  useCanvasLoop(canvasRef, draw, {
    running: true,
    maxDpr: 2
  });
  return <div className={`relative w-full h-[260px] sm:h-[320px] md:h-[380px] rounded-xl overflow-hidden border border-border bg-card/40 backdrop-blur-md flex items-center justify-center ${className}`}>
      <canvas ref={canvasRef} aria-label="Interactive 3D model of a rotating galactic disc with coordinate grid rings" className="w-full h-full block" />

      {/* Subtle corner telemetry telemetry marks (real geometry, no fake console text) */}
      <div className="absolute top-3 left-3 pointer-events-none font-mono text-[0.6875rem] text-primary/70 tracking-widest uppercase">
        MODEL: DISC_ROTATION // KINEMATICS
      </div>
      <div className="absolute bottom-3 right-3 pointer-events-none font-mono text-[0.6875rem] text-muted-foreground/70 tracking-wider">
        PLANCK LIMIT: 1.616&times;10⁻³⁵ m
      </div>
    </div>;
}